import React, { useState, useEffect } from 'react';
import { Column } from '@nextiva/crm-datatable';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { get } from 'lodash';
import {
  SimpleModal,
  SimpleSnackbar,
  styled,
  Icons,
  Icon,
} from '@nextiva/next-ui-components';
import { cancelRequest } from 'utils/redux-axios-helpers';
import { DATE_FORMATS } from 'utils/date-helpers';
import {
  TableActionsPanel,
  withRouterQuery,
  ContentRow,
  AdminTable,
} from 'components';
import { encodeDateFilter, decodeDateFilter } from 'components/withRouterQuery';
import {
  Page,
  CREATE_PLATFORM_ROLE,
  UPDATE_PLATFORM_ROLE,
  DELETE_PLATFORM_ROLE,
} from 'containers';
import {
  cellRenderer,
  dateTimeRenderer,
  tagRenderer,
  withHintRenderer,
  Actions,
} from 'components/tableRenderers';
import { useSnackbar } from 'hooks';
import { getRoles, deleteRole } from './duck/actions';
import styles from './ManageRoles.scss';

const StyledModal = styled(SimpleModal)`
  z-index: 9999;
  & .MuiDialog-paper {
    min-width: 17%;
    padding: 16px;
  }
  & .MuiDialogContent-root {
    text-align: center;
  }
  & .MuiButtonBase-root.MuiButton-containedPrimary {
    background-color: #f8b400;
  }
  & .MuiButtonBase-root.MuiButton-containedPrimary:hover {
    background-color: #f8b400;
  }
  & .MuiTypography-root {
    padding: 20px 0;
  }
`;

const StyledIcon = styled(Icon)`
  font-size: 32px;
  color: #f8b400;
  text-align: center;
`;

const displayPreferences = [
  {
    name: 'filter_by_name',
    valueName: 'name',
    type: 'text',
    label: 'Role Name',
    display: true,
    width: '20%',
    cellRenderer: tagRenderer,
    shouldRenderFilter: true,
    shouldRenderSort: true,
    typeConfig: {
      defaultOperator: 'contains',
      operatorOptions: [
        {
          value: 'contains',
          label: 'Role Name contains',
        },
      ],
    },
  },
  {
    name: 'assignedProfilesCount',
    value: 'assignedProfilesCount',
    type: 'text',
    label: 'No. Assigned',
    display: true,
    width: '20%',
    cellRenderer: ({ cellData }) => <span>{cellData || 0}</span>,
  },
  {
    name: 'filter_by_description',
    valueName: 'description',
    value: 'description',
    type: 'text',
    label: 'Description',
    display: true,
    width: '40%',
    cellRenderer: withHintRenderer,
    shouldRenderFilter: true,
    shouldRenderSort: true,
    typeConfig: {
      defaultOperator: 'contains',
      operatorOptions: [
        {
          value: 'contains',
          label: 'Description contains',
        },
      ],
    },
  },
  {
    name: 'filter_by_creation_date',
    valueName: 'creationDate',
    type: 'date',
    label: 'Date Created',
    display: true,
    width: '20%',
    cellRenderer: dateTimeRenderer(DATE_FORMATS.MICRO),
    shouldRenderFilter: true,
    shouldRenderSort: true,
    typeConfig: {
      defaultOperator: 'eq',
      operatorOptions: [
        {
          value: 'eq',
          label: 'Date Created is exactly',
        },
      ],
    },
    encodeFilter: encodeDateFilter(DATE_FORMATS.DEFAULT, DATE_FORMATS.SHORT),
    decodeFilter: decodeDateFilter(DATE_FORMATS.SHORT, DATE_FORMATS.DEFAULT),
  },
];

const actions = [
  { label: 'Clone', type: 'clone', permissions: [CREATE_PLATFORM_ROLE] },
  { label: 'Edit', type: 'edit', permissions: [UPDATE_PLATFORM_ROLE] },
  { label: 'Delete', type: 'delete', permissions: [DELETE_PLATFORM_ROLE] },
];

const filterDecoders = displayPreferences.reduce(
  (res, { name, decodeFilter }) => ({ ...res, [name]: decodeFilter }),
  {},
);

const ManageRoles = ({
  getTableQuery,
  defaultQuery,
  isNewQuery,
  query,
  filtersFromParams,
  handleFiltersChange,
  orderFromParams,
  handleSortChange,
  handleLimitChange,
  handlePageChange,
}) => {
  const dispatch = useDispatch();
  const roles = useSelector((state) => state.roles);
  const [openModal, setOpenModal] = useState(false);
  const [data, setData] = useState(null);
  const { limit, filters, sort } = getTableQuery();
  const { limit: defaultLimit, sort: defaultSort } = query;
  const pageCount = Math.ceil(roles.totalCount / (limit || defaultLimit));
  const setSnackbar = useSnackbar();
  useEffect(() => {
    const promise = dispatch(
      getRoles({ ...defaultQuery, ...getTableQuery() }, setSnackbar),
    );

    return () => {
      dispatch(cancelRequest(promise));
    };
  }, [dispatch, query]);

  useEffect(() => {
    if (isNewQuery) {
      const promise = dispatch(
        getRoles(
          {
            ...query,
            ...getTableQuery(),
          },
          setSnackbar,
        ),
      );

      return () => {
        dispatch(cancelRequest(promise));
      };
    }
  }, [dispatch, query]);

  const openPage = ({ name }, actionType) => {
    window.location.hash = `/advancedroles/${actionType}/${encodeURIComponent(
      name,
    )}`;
  };

  const handleAction = (data, type) => {
    switch (type) {
      case 'delete':
        setOpenModal(true);
        setData(data);
        break;
      default:
        openPage(data, type);
    }
  };

  const handleRowClick = (e, data) => {
    openPage(data, 'edit');
  };

  const closeModal = () => {
    setOpenModal(false);
  };

  const message = (
    <>
      <div>
        <div className={styles.headerMessage}>
          <div>
            <strong>Warning: </strong>
            <span>Proceed with caution.</span>
          </div>
        </div>
      </div>
      <p>
        Editing roles from this page may lead to an inability to view and act on
        records, like Cases or Contacts. To configure permissions for a role
        please visit the{' '}
        <Link to="roles" data-testid="PlatformAdminUI-ManageRoles-Link">
          roles and permissions
        </Link>{' '}
        page. <br /> These advanced permissions are available for configuring
        permissions for specific fields, please only proceed to set field
        permissions.
      </p>
    </>
  );

  return (
    <Page title="Roles & Permissions" className={styles.pageManageRoles}>
      <StyledModal
        onClose={closeModal}
        open={openModal}
        title="Are you sure?"
        confirmText="Save"
        cancelText="Cancel"
        cancelButtonProps={{ onClick: closeModal }}
        confirmButtonProps={{
          onClick: () => dispatch(deleteRole(data, setSnackbar, setOpenModal)),
        }}
      >
        <StyledIcon icon={Icons.info} />
      </StyledModal>
      <SimpleSnackbar
        anchorOrigin={{
          horizontal: 'center',
          vertical: 'bottom',
        }}
        autoHideDuration={null}
        variant="error"
        message={message}
      />
      <ContentRow>
        <TableActionsPanel
          createLink="/advancedroles/create/role"
          permissions={[CREATE_PLATFORM_ROLE]}
          onChangeLimit={handleLimitChange}
          pageCount={pageCount}
          defaultLimit={limit || defaultLimit}
          onPageChange={!!pageCount && handlePageChange}
        />
        <div className={styles.tableWrapper}>
          <AdminTable
            isLoading={roles.loading === 'list'}
            data={roles.list}
            displayPreferences={displayPreferences}
            disableGear
            onRowClick={handleRowClick}
            disableCheckboxes
            renderActions={(data) => {
              return (
                <Actions
                  actions={actions}
                  data={data}
                  onSelect={handleAction}
                />
              );
            }}
            onFiltersChange={(data) =>
              handleFiltersChange(filterDecoders)(data)
            }
            onSortChange={handleSortChange}
          >
            {displayPreferences.map((item, index) => {
              return (
                <Column
                  key={index}
                  name={item.name}
                  type={item.type}
                  typeConfig={item.typeConfig}
                  cellRenderer={item.cellRenderer || cellRenderer}
                  getCellData={(rowData, name) =>
                    get(rowData, item.valueName || name)
                  }
                  shouldRenderSort={get(item, 'shouldRenderSort', false)}
                  order={orderFromParams(sort || defaultSort, item.name)}
                  shouldRenderFilter={get(item, 'shouldRenderFilter', false)}
                  filterData={filtersFromParams(
                    filters,
                    item.name,
                    item.encodeFilter,
                  )}
                >
                  {item.label}
                </Column>
              );
            })}
          </AdminTable>
        </div>
      </ContentRow>
    </Page>
  );
};

ManageRoles.defaultProps = {
  query: {
    page: 1,
    limit: 50,
    sort: 'filter_by_creation_date,desc',
  },
};

export default withRouterQuery(ManageRoles);
